import {
  AnnotationWhereGroup,
  isAnnotationWhereGroup,
} from "components/annotations/AnnotationWhereGroup";
import { isMosaicTrafficLightsValue, MosaicTrafficLightsValue } from "../../traffic-lights";
import { AitCellData, isAitCellData } from "./AitCellData";

/**
 * Data for a single table row.
 *
 * @template T The data type used for cell text and comments.
 */

export interface AitRowData<T> {
  /** Unique identifier for the row. */
  aitid: string;
  /** Optional repeat identifier for repeated rows. */
  rowRepeat?: string;
  /** Cells contained in the row. */
  cells: AitCellData<T>[];
  /** When true, include a space after this row. */
  spaceAfter?: boolean;
  /** Traffic light value representing the row's core status. */
  coreTrafficLight: MosaicTrafficLightsValue;
  /** Equivalent AIT id when this row originates from a template. */
  templateAitId?: string;
  /** Optional row-level annotation. */
  annotation?: AnnotationWhereGroup;
}

/**
 * Type guard that asserts whether a value conforms to the AitRowData shape.
 *
 * @param obj - The object to check
 * @param isT - Type guard for the generic type T
 * @param noColumnIdCheck - Optional flag to skip column ID checks (used for row templates)
 * @returns boolean
 */
export const isAitRowData = <T>(
  obj: unknown,
  isT: (obj: unknown) => boolean,
  noColumnIdCheck?: boolean,
): obj is AitRowData<T> => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check basic property
  const chkObj = obj as AitRowData<T>;
  if (typeof chkObj.aitid !== "string") return false;
  if (typeof chkObj.rowRepeat !== "undefined" && typeof chkObj.rowRepeat !== "string") return false;
  if (typeof chkObj.spaceAfter !== "undefined" && typeof chkObj.spaceAfter !== "boolean")
    return false;
  if (typeof chkObj.templateAitId !== "undefined" && typeof chkObj.templateAitId !== "string")
    return false;
  // Check arrays
  if (
    !Array.isArray(chkObj.cells) ||
    chkObj.cells.some((cell) => !isAitCellData(cell, isT, noColumnIdCheck))
  )
    return false;
  // Check types
  if (!isMosaicTrafficLightsValue(chkObj.coreTrafficLight)) return false;
  // Check lower level objects
  if (typeof chkObj.annotation !== "undefined" && !isAnnotationWhereGroup(chkObj.annotation))
    return false;
  // Everything is good
  return true;
};
